import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Router, RouterLink } from '@angular/router';
import { NgIf } from '@angular/common';

import { TranslatePipe } from '../core/pipes/translate.pipe';
import { AuthShowcaseComponent } from '../core/ui/auth-showcase/auth-showcase.component';
import { LanguageSwitchComponent } from '../core/ui/language-switch/language-switch.component';
import { AccessControlService, AuthService, LanguageService, TokenStorageService } from './../core/services';
import { APP_OTP_EXPIRY_SECONDS } from '../core/constants/app-constants';

type LocalizedCopy = {
  bn: string;
  en: string;
};

type IconMarkup = string | SafeHtml;

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [FormsModule, RouterLink, NgIf, TranslatePipe, LanguageSwitchComponent, AuthShowcaseComponent],
  templateUrl: './sign-in.html',
  styleUrls: ['./sign-in.css']
})
export class SignInComponent implements AfterViewInit, OnDestroy {
  private readonly featureGridTransitionMs = 0;
  private readonly rememberedEmailStorageKey = 'htally_remembered_email';
  private readonly rememberedPasswordStorageKey = 'htally_remembered_password';
  title = '';
  isLoading = false;
  isAuthenticated = false;
  error: string | null = null;
  status: string | null = null;
  isForgotPasswordModalOpen = false;
  isSendingResetOtp = false;
  isResettingPassword = false;
  isResetOtpSent = false;
  resetError: string | null = null;
  resetStatus: string | null = null;
  resetOtpExpiresInSeconds = 0;
  private resetOtpCountdownSub: Subscription | null = null;
  private featureVisibilityFrame: number | null = null;
  private featuresResizeObserver: ResizeObserver | null = null;
  private featureGridTransitionTimeout: ReturnType<typeof setTimeout> | null = null;
  private deferredFeatureVisibilityTimeouts: Array<ReturnType<typeof setTimeout>> = [];
  private featureGridColumns = 1;
  private featureGridRowGap = 0;
  private featureGridTileHeight = 72;
  private isFeatureGridTransitioning = false;

  @ViewChild('featuresCard') private featuresCardRef?: ElementRef<HTMLElement>;
  @ViewChild('featuresHeading') private featuresHeadingRef?: ElementRef<HTMLElement>;
  @ViewChild('featureGrid') private featureGridRef?: ElementRef<HTMLElement>;
  credentials = {
    email: '',
    password: ''
  };
  rememberMe = false;

  resetForm = {
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  };

  readonly showcaseTitle: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যার',
    en: 'Flat management software'
  };

  readonly showcaseDescription: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্টের সকল কাজকর্ম এক প্ল্যাটফর্মে সহজ, স্বচ্ছ ও নিখুঁতভাবে পরিচালনা করুন।',
    en: 'Manage house, flat operations from one platform with a clear and organized workflow.'
  };

  readonly featureHeading: LocalizedCopy = {
    bn: 'আমাদের ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যারটিতে যা যা থাকছে:',
    en: 'Included in our flat management software:'
  };

  isFeaturesExpanded = false;
  renderExpandedFeatures = false;
  collapsedFeatureCount = 6;
  featureGridMaxHeight: number | null = null;

  toggleFeatures() {
    if (this.isFeatureGridTransitioning) {
      return;
    }

    const grid = this.featureGridRef?.nativeElement;
    const currentHeight = Math.ceil(this.featureGridRef?.nativeElement.getBoundingClientRect().height ?? 0);
    if (currentHeight > 0) {
      this.featureGridMaxHeight = currentHeight;
    }

    if (!grid || typeof window === 'undefined') {
      this.renderExpandedFeatures = !this.renderExpandedFeatures;
      this.isFeaturesExpanded = this.renderExpandedFeatures;
      this.scheduleFeatureVisibilityUpdate(true);
      return;
    }

    this.isFeatureGridTransitioning = true;

    if (this.isFeaturesExpanded) {
      const collapsedHeight = this.getCollapsedGridHeight();
      window.requestAnimationFrame(() => {
        this.featureGridMaxHeight = collapsedHeight;
      });

      this.featureGridTransitionTimeout = setTimeout(() => {
        this.featureGridTransitionTimeout = null;
        this.renderExpandedFeatures = false;
        this.isFeaturesExpanded = false;
        this.isFeatureGridTransitioning = false;
        this.scheduleFeatureVisibilityUpdate();
      }, this.featureGridTransitionMs);
      return;
    }

    this.renderExpandedFeatures = true;
    this.isFeaturesExpanded = true;
    window.requestAnimationFrame(() => {
      const expandedHeight = Math.ceil(grid.scrollHeight || grid.getBoundingClientRect().height || 0);
      this.featureGridMaxHeight = expandedHeight > 0 ? expandedHeight : null;
    });

    this.featureGridTransitionTimeout = setTimeout(() => {
      this.featureGridTransitionTimeout = null;
      this.isFeatureGridTransitioning = false;
      this.applyFeatureGridHeight(false);
    }, this.featureGridTransitionMs);
  }

  readonly features: Array<{ icon: IconMarkup; title: LocalizedCopy }> = [
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2L4 9v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-8-7zm0 13a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>`,
      title: { bn: 'বাড়ি পরিচালনা', en: 'House Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5zm12 4H9v2h6V9zm0 4H9v2h6v-2z"/></svg>`,
      title: { bn: 'ফ্ল্যাট পরিচালনা', en: 'Flat Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M8 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6Z"/><path d="M16.5 10a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5Z"/><path d="M3.5 18.5c.8-2 2.9-3.3 5.5-3.3s4.7 1.3 5.5 3.3"/><path d="M13 17.8c.7-1.5 2.3-2.5 4.3-2.5c1.7 0 3.2.7 4.2 2"/></svg>`,
      title: { bn: 'ভাড়াটিয়া পরিচালনা', en: 'Renter Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 14h-2v-2h2v2zm3.5-4h-5v-2h5v2zm0-4h-5V7h5v2z"/></svg>`,
      title: { bn: 'ভাড়া ও খরচ ট্র্যাকিং', en: 'Rent & Expense Tracking' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>`,
      title: { bn: 'স্মার্ট ড্যাশবোর্ড', en: 'Smart Dashboard' }
    },
    {
      icon: `<svg width="22" height="20" viewBox="0 0 22 20" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.54248C1 2.586 2.586 1 4.54248 1H17.4575C19.414 1 21 2.586 21 4.54248V15.4575C21 17.414 19.414 19 17.4575 19H4.54248C2.586 19 1 17.414 1 15.4575V4.54248ZM4.54248 3.51863H17.4575C18.0229 3.51863 18.4814 3.97708 18.4814 4.54248V5.00643H3.51863V4.54248C3.51863 3.97708 3.97708 3.51863 4.54248 3.51863ZM18.4814 7.52494H3.51863V15.4575C3.51863 16.0229 3.97708 16.4814 4.54248 16.4814H17.4575C18.0229 16.4814 18.4814 16.0229 18.4814 15.4575V7.52494ZM6.061 10.0434H11.5367V11.3323H6.061V10.0434ZM15.939 10.0434H13.4722V11.3323H15.939V10.0434ZM6.061 12.6212H15.939V13.9101H6.061V12.6212Z" /></svg>`,
      title: { bn: 'রশিদ পরিচালনা', en: 'Receipt Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>`,
      title: { bn: 'আর্থিক সারসংক্ষেপ', en: 'Financial Summary' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8c-2.95 3.3-5.2 5.98-5.2 9.03A5.2 5.2 0 0 0 12 17.03a5.2 5.2 0 0 0 5.2-5.2c0-3.05-2.25-5.73-5.2-9.03Zm1.05 8.36h-1.47l.58-2.07c.05-.18-.15-.31-.29-.18l-2.38 2.27h1.47l-.58 2.07c-.05.18.15.31.29.18l2.38-2.27ZM8.9 20.5h6.2V19h-6.2v1.5Z"/></svg>`,
      title: { bn: 'ইউটিলিটি বিল পরিচালনা', en: 'Utility Bill Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21.1 7.2l-4.3-4.3a1.2 1.2 0 0 0-1.7 0l-1.6 1.6l1.8 1.8l-1.4 1.4l-3-3a1.2 1.2 0 0 0-1.7 0L8 5.8c-.47.47-.47 1.24 0 1.7l1.5 1.5l-5.8 5.8a2.2 2.2 0 0 0 0 3.1l1.4 1.4a2.2 2.2 0 0 0 3.1 0l5.8-5.8l1.5 1.5c.47.47 1.24.47 1.7 0l1.1-1.1a1.2 1.2 0 0 0 0-1.7l-3-3l1.4-1.4l1.8 1.8l1.6-1.6c.47-.47.47-1.24 0-1.7Z"/></svg>`,
      title: { bn: 'রক্ষণাবেক্ষণ খরচ ট্র্যাকিং', en: 'Maintenance Cost Tracking' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.9 6.5H5.1l-1.3 4h17.4l-2.3-4zM4.1 12v6c0 1.1.9 2 2 2h11.8c1.1 0 2-.9 2-2v-6H4.1zm5.9 6H7.9v-4h2.1v4zm6.1 0h-2.1v-4h2.1v4z"/></svg>`,
      title: { bn: 'গ্যারেজ পরিচালনা', en: 'Garage Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-5h2v5zm4 0h-2v-3h2v3zm4 0h-2v-5h2v5z"/></svg>`,
      title: { bn: 'রিপোর্টিং সিস্টেম', en: 'Reporting System' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z"/></svg>`,
      title: { bn: 'কর্মচারী পরিচালনা (শীঘ্রই)', en: 'Employee Management (Coming Soon)' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z"/></svg>`,
      title: { bn: 'বিল নোটিশ সিস্টেম (শীঘ্রই)', en: 'Bill Notice System (Coming Soon)' }
    }
  ];

  readonly rolesHeading: LocalizedCopy = {
    bn: 'ইউজার লগইন প্যানেলে:',
    en: 'User login panel:'
  };

  readonly roles: Array<{ icon: IconMarkup; text: LocalizedCopy }> = [
    {
      icon: `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l6 2.7v5.4c0 4-2.5 7.7-6 8.9c-3.5-1.2-6-4.9-6-8.9V5.7L12 3Z"/><path d="M12 9.2a1.9 1.9 0 1 0 0-3.8a1.9 1.9 0 0 0 0 3.8Z"/><path d="M9.3 14.5c.5-1.4 1.5-2.1 2.7-2.1s2.2.7 2.7 2.1"/></svg>`,
      text: { bn: 'অ্যাডমিন (এপার্টমেন্ট মালিক)', en: 'Admin (flat owner)' }
    },
    {
      icon: `<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M4 7a3 3 0 1 1 6 0a3 3 0 0 1-6 0z"/><path d="M14 7a3 3 0 1 1 6 0a3 3 0 0 1-6 0z"/><path d="M4 17a3 3 0 1 1 6 0a3 3 0 0 1-6 0z"/><path d="M15 16h4a1 1 0 1 1 0 2h-4a1 1 0 1 1 0-2z"/></svg>`,
      text: { bn: 'সাবইউজার (পরিচালনাকারী)', en: 'Subuser (manager)' }
    },
    {
      icon: `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M10 11a3.5 3.5 0 1 0 0-7a3.5 3.5 0 0 0 0 7Z"/><path d="M4 19c.8-2.3 3-3.8 6-3.8s5.2 1.5 6 3.8"/><path d="M17 8h3"/><path d="M18.5 6.5v3"/><circle cx="18.5" cy="8" r="2.5"/></svg>`,
      text: { bn: 'ভাড়াটিয়া', en: 'Renter' }
    }
  ];

  readonly footerNote: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যারটি আপনাকে এক নজরে আপনার হাতে পুরো ফ্ল্যাটের নিয়ন্ত্রণ করতে সাহায্য করবে।',
    en: 'The software helps you control your flat operations from one dashboard.'
  };

  constructor(
    private readonly auth: AuthService,
    private readonly access: AccessControlService,
    private readonly tokens: TokenStorageService,
    private readonly language: LanguageService,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer
  ) {
    this.features.forEach((feature) => {
      feature.icon = this.sanitizer.bypassSecurityTrustHtml(`${feature.icon}`);
    });
    this.roles.forEach((role) => {
      role.icon = this.sanitizer.bypassSecurityTrustHtml(`${role.icon}`);
    });
    this.updateFeatureVisibility();
    this.isAuthenticated = this.tokens.hasActiveSession;
    this.restoreRememberedCredentials();
    if (this.isAuthenticated) {
      this.status = this.t('signIn.status.alreadyLoggedIn');
    }
  }

  ngAfterViewInit(): void {
    this.scheduleFeatureVisibilityUpdate();
    this.scheduleDeferredFeatureVisibilityUpdates();

    if (typeof ResizeObserver === 'undefined' || !this.featuresCardRef?.nativeElement) {
      return;
    }

    this.featuresResizeObserver = new ResizeObserver(() => this.scheduleFeatureVisibilityUpdate());
    this.featuresResizeObserver.observe(this.featuresCardRef.nativeElement);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleFeatureVisibilityUpdate();
  }

  ngOnDestroy(): void {
    this.resetOtpCountdownSub?.unsubscribe();
    this.featuresResizeObserver?.disconnect();
    if (this.featureVisibilityFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.featureVisibilityFrame);
    }
    if (this.featureGridTransitionTimeout !== null) {
      clearTimeout(this.featureGridTransitionTimeout);
    }
    this.deferredFeatureVisibilityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
  }

  login(): void {
    const email = `${this.credentials.email ?? ''}`.trim();

    if (!email || !this.credentials.password) {
      this.error = this.t('signIn.error.requiredCredentials');
      return;
    }

    this.credentials.email = email;
    this.isLoading = true;
    this.error = null;
    this.status = null;

    this.auth
      .login({
        email,
        password: this.credentials.password,
        rememberSession: this.rememberMe
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.isAuthenticated = true;
          this.status = this.t('signIn.status.loginSuccess');
          this.persistRememberedCredentials();
          this.access.refresh().subscribe({
            next: () => {
              setTimeout(() => {
                this.router.navigate([this.resolvePostLoginRoute()]);
              }, 800);
            },
            error: () => {
              setTimeout(() => {
                this.router.navigate(['/dashboard']);
              }, 800);
            }
          });
        },
        error: err => {
          console.error(err);
          this.error = this.resolveLoginErrorMessage(err);
        }
      });
  }

  get resetOtpExpireLabel(): string {
    if (!this.resetOtpExpiresInSeconds) {
      return '00:00';
    }
    const min = Math.floor(this.resetOtpExpiresInSeconds / 60);
    const sec = this.resetOtpExpiresInSeconds % 60;
    return `${`${min}`.padStart(2, '0')}:${`${sec}`.padStart(2, '0')}`;
  }

  get isBangla(): boolean {
    return this.language.currentLanguage === 'bn';
  }

  get visibleFeatures() {
    return this.renderExpandedFeatures ? this.features : this.features.slice(0, this.collapsedFeatureCount);
  }

  get shouldShowFeatureToggle(): boolean {
    return this.features.length > this.collapsedFeatureCount;
  }

  openForgotPasswordModal(): void {
    this.isForgotPasswordModalOpen = true;
    this.resetError = null;
    this.resetStatus = null;
    this.resetForm.email = this.credentials.email;
  }

  closeForgotPasswordModal(event?: Event): void {
    event?.stopPropagation();
    this.isForgotPasswordModalOpen = false;
    this.resetError = null;
    this.resetStatus = null;
    this.resetOtpCountdownSub?.unsubscribe();
    this.resetOtpExpiresInSeconds = 0;
    this.isSendingResetOtp = false;
    this.isResettingPassword = false;
    this.isResetOtpSent = false;
    this.resetForm = {
      email: this.credentials.email,
      otp: '',
      newPassword: '',
      confirmPassword: ''
    };
  }

  sendPasswordResetOtp(): void {
    const email = `${this.resetForm.email ?? ''}`.trim();
    if (!email) {
      this.resetError = this.t('signIn.error.emailRequired');
      return;
    }

    this.isSendingResetOtp = true;
    this.resetError = null;
    this.resetStatus = null;

    this.auth.requestPasswordResetOtp({ email })
      .pipe(finalize(() => (this.isSendingResetOtp = false)))
      .subscribe({
        next: (res: any) => {
          const responseCode = `${res?.code ?? ''}`.trim();
          if (responseCode && responseCode !== '000') {
            this.resetError = `${res?.message ?? this.t('signIn.error.sendOtpFailed')}`;
            return;
          }
          this.isResetOtpSent = true;
          this.resetStatus = `${res?.message ?? this.t('signIn.status.otpSent')}`;
          this.startResetOtpCountdown();
        },
        error: (err: any) => {
          this.resetError = `${err?.error?.message ?? err?.message ?? this.t('signIn.error.sendOtpFailed')}`;
        }
      });
  }

  submitPasswordReset(): void {
    const email = `${this.resetForm.email ?? ''}`.trim();
    const otp = `${this.resetForm.otp ?? ''}`.trim();
    const newPassword = `${this.resetForm.newPassword ?? ''}`;
    const confirmPassword = `${this.resetForm.confirmPassword ?? ''}`;

    if (!email) {
      this.resetError = this.t('signIn.error.emailRequired');
      return;
    }
    if (!otp) {
      this.resetError = this.t('signIn.error.otpRequired');
      return;
    }
    if (!this.isResetOtpSent) {
      this.resetError = this.t('signIn.error.sendOtpFirst');
      return;
    }
    if (!/^[0-9]{4,8}$/.test(otp)) {
      this.resetError = this.t('signIn.error.invalidOtp');
      return;
    }
    if (this.resetOtpExpiresInSeconds === 0) {
      this.resetError = this.t('signIn.error.otpExpired');
      return;
    }
    if (!newPassword) {
      this.resetError = this.t('signIn.error.newPasswordRequired');
      return;
    }
    if (newPassword.length < 6) {
      this.resetError = this.t('signIn.error.passwordMinLength');
      return;
    }
    if (!confirmPassword) {
      this.resetError = this.t('signIn.error.confirmPasswordRequired');
      return;
    }
    if (newPassword !== confirmPassword) {
      this.resetError = this.t('signIn.error.passwordMismatch');
      return;
    }

    this.isResettingPassword = true;
    this.resetError = null;
    this.resetStatus = null;

    this.auth.resetPassword({ email, otp, newPassword })
      .pipe(finalize(() => (this.isResettingPassword = false)))
      .subscribe({
        next: (res: any) => {
          if (res?.code !== '000') {
            this.resetError = `${res?.message ?? this.t('signIn.error.resetPasswordFailed')}`;
            return;
          }
          this.credentials.email = email;
          this.credentials.password = newPassword;
          this.status = `${res?.message ?? this.t('signIn.status.passwordResetSuccess')}`;
          this.closeForgotPasswordModal();
        },
        error: (err: any) => {
          this.resetError = `${err?.error?.message ?? err?.message ?? this.t('signIn.error.resetPasswordFailed')}`;
        }
      });
  }

  private startResetOtpCountdown(): void {
    this.resetOtpCountdownSub?.unsubscribe();
    this.resetOtpExpiresInSeconds = APP_OTP_EXPIRY_SECONDS;
    this.resetOtpCountdownSub = interval(1000).subscribe(() => {
      if (this.resetOtpExpiresInSeconds <= 1) {
        this.resetOtpExpiresInSeconds = 0;
        this.resetOtpCountdownSub?.unsubscribe();
        return;
      }
      this.resetOtpExpiresInSeconds -= 1;
    });
  }

  private resolveLoginErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401 || error.status === 403) {
        return this.t('signIn.error.invalidCredentials');
      }

      const serverMessage = `${error.error?.message ?? ''}`.trim();
      if (serverMessage) {
        return serverMessage;
      }

      if (error.status === 0) {
        return this.t('signIn.error.serverUnavailable');
      }
    }

    if (error instanceof Error) {
      const message = `${error.message ?? ''}`.trim();
      if (message.startsWith('401 -') || message.startsWith('403 -')) {
        return this.t('signIn.error.invalidCredentials');
      }
      if (message) {
        return message;
      }
    }

    return this.t('signIn.error.signInFailed');
  }

  private t(key: string, fallback = ''): string {
    return this.language.translate(key, fallback);
  }

  localize(copy: LocalizedCopy): string {
    return this.isBangla ? copy.bn : copy.en;
  }

  roleHelpSegments(role: { text: LocalizedCopy }): Array<{ text: string; isKeyword?: boolean }> {
    if (role.text.en === 'Subuser (manager)') {
      return this.markUsername('Admin can create subusers. The username is available in the subuser details. Initially the username and password are the same. A subuser can update their password later and can work like their admin.');
    }

    if (role.text.en === 'Renter') {
      return this.markUsername('Renters can log in and see their transactions, payment receipts, and related payment information. The renter username is available in the renter details.');
    }

    return [];
  }

  private markUsername(text: string): Array<{ text: string; isKeyword?: boolean }> {
    return text.split(/(username)/gi)
      .filter((segment) => segment.length > 0)
      .map((segment) => ({
        text: segment,
        isKeyword: segment.toLowerCase() === 'username'
      }));
  }

  private scheduleFeatureVisibilityUpdate(animate = false): void {
    if (typeof window === 'undefined') {
      this.updateFeatureVisibility(animate);
      return;
    }

    if (this.featureVisibilityFrame !== null) {
      window.cancelAnimationFrame(this.featureVisibilityFrame);
    }

    this.featureVisibilityFrame = window.requestAnimationFrame(() => {
      this.featureVisibilityFrame = null;
      this.updateFeatureVisibility(animate);
    });
  }

  private scheduleDeferredFeatureVisibilityUpdates(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.deferredFeatureVisibilityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.deferredFeatureVisibilityTimeouts = [
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 0),
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 180),
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 420)
    ];
  }

  private updateFeatureVisibility(animate = false): void {
    if (typeof window === 'undefined') {
      this.collapsedFeatureCount = Math.min(this.features.length, 6);
      return;
    }

    const featureGrid = this.featureGridRef?.nativeElement;
    const featuresCard = this.featuresCardRef?.nativeElement;
    const featuresHeading = this.featuresHeadingRef?.nativeElement;

    const isMobileWidth = window.innerWidth <= 640;
    let columns = isMobileWidth ? 1 : window.innerWidth <= 1180 ? 2 : 3;
    let rows = window.innerHeight <= 760 ? 2 : window.innerHeight <= 980 ? 3 : 4;
    const minimumRows = isMobileWidth ? 3 : 1;

    if (featureGrid && featuresCard && featuresHeading) {
      const gridStyle = window.getComputedStyle(featureGrid);
      const cardStyle = window.getComputedStyle(featuresCard);
      const headingStyle = window.getComputedStyle(featuresHeading);
      const sampleFeature = featureGrid.querySelector<HTMLElement>('.auth-showcase__feature');
      const sampleButton = featureGrid.querySelector<HTMLElement>('.auth-showcase__more-btn--tile');
      const trackList = gridStyle.gridTemplateColumns.split(' ').filter(Boolean);
      const measuredColumns = trackList.length;
      const rowGap = Number.parseFloat(gridStyle.rowGap || '0') || 0;
      const cardPaddingTop = Number.parseFloat(cardStyle.paddingTop || '0') || 0;
      const cardPaddingBottom = Number.parseFloat(cardStyle.paddingBottom || '0') || 0;
      const headingMarginBottom = Number.parseFloat(headingStyle.marginBottom || '0') || 0;
      const tileHeight = Math.max(
        sampleFeature?.getBoundingClientRect().height ?? 0,
        sampleButton?.getBoundingClientRect().height ?? 0,
        window.innerWidth <= 640 ? 76 : 72
      );
      const availableGridHeight = featuresCard.clientHeight - cardPaddingTop - cardPaddingBottom - featuresHeading.clientHeight - headingMarginBottom;

      if (measuredColumns > 0) {
        columns = measuredColumns;
      }

      if (availableGridHeight > 0 && tileHeight > 0) {
        rows = Math.max(minimumRows, Math.floor((availableGridHeight + rowGap) / (tileHeight + rowGap)));
      }
    }

    rows = Math.max(rows, minimumRows);

    this.featureGridColumns = Math.max(columns, 1);
    this.featureGridRowGap = Math.max(this.featureGridRowGap, 0);
    this.featureGridTileHeight = Math.max(this.featureGridTileHeight, 1);

    const totalSlots = Math.max(columns * rows, columns === 1 ? 2 : columns);
    const needsToggle = this.features.length > totalSlots;

    this.collapsedFeatureCount = Math.min(this.features.length, needsToggle ? Math.max(totalSlots - 1, 1) : totalSlots);
    if (!this.isFeatureGridTransitioning) {
      this.applyFeatureGridHeight(animate);
    }
  }

  private applyFeatureGridHeight(animate: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    const grid = this.featureGridRef?.nativeElement;
    if (!grid) {
      return;
    }

    if (this.featureGridTransitionTimeout !== null) {
      clearTimeout(this.featureGridTransitionTimeout);
      this.featureGridTransitionTimeout = null;
    }

    const setTargetHeight = () => {
      const targetHeight = Math.ceil(grid.scrollHeight || grid.getBoundingClientRect().height || 0);
      this.featureGridMaxHeight = targetHeight > 0 ? targetHeight : null;
    };

    if (!animate) {
      setTargetHeight();
      return;
    }

    window.requestAnimationFrame(() => {
      setTargetHeight();
      this.featureGridTransitionTimeout = setTimeout(() => {
        this.featureGridTransitionTimeout = null;
        setTargetHeight();
      }, this.featureGridTransitionMs);
    });
  }

  private getCollapsedGridHeight(): number | null {
    const columns = Math.max(this.featureGridColumns, 1);
    const itemsInCollapsedView = this.collapsedFeatureCount + (this.features.length > this.collapsedFeatureCount ? 1 : 0);
    const rows = Math.max(1, Math.ceil(itemsInCollapsedView / columns));
    const collapsedHeight = rows * this.featureGridTileHeight + Math.max(0, rows - 1) * this.featureGridRowGap;
    return collapsedHeight > 0 ? Math.ceil(collapsedHeight) : null;
  }

  private restoreRememberedCredentials(): void {
    const storage = this.getLocalStorage();
    if (!storage) {
      return;
    }

    const rememberedEmail = `${storage.getItem(this.rememberedEmailStorageKey) ?? ''}`.trim();
    const rememberedPassword = `${storage.getItem(this.rememberedPasswordStorageKey) ?? ''}`;

    if (!rememberedEmail && !rememberedPassword) {
      return;
    }

    this.credentials.email = rememberedEmail;
    this.credentials.password = rememberedPassword;
    this.rememberMe = true;
  }

  private persistRememberedCredentials(): void {
    const storage = this.getLocalStorage();
    if (!storage) {
      return;
    }

    if (this.rememberMe) {
      const email = `${this.credentials.email ?? ''}`.trim();
      const password = `${this.credentials.password ?? ''}`;

      if (email) {
        storage.setItem(this.rememberedEmailStorageKey, email);
        storage.setItem(this.rememberedPasswordStorageKey, password);
        return;
      }
    }

    storage.removeItem(this.rememberedEmailStorageKey);
    storage.removeItem(this.rememberedPasswordStorageKey);
  }

  private getLocalStorage(): Storage | null {
    return typeof window === 'undefined' ? null : window.localStorage;
  }

  private resolvePostLoginRoute(): string {
    const route = this.access.defaultRoute();
    return route === '/signIn' ? '/dashboard' : route;
  }
}
